import React, { useEffect, useState } from "react";
import { ItemWithMetaData, Rating } from "../api/Items";
import { MediaExternalInternalURLs, PostImage } from "../posts/PostImage";
import Button, { ButtonColorTypes } from "../shared-components/Button";
import ErrorMessageContainer from "../shared-components/ErrorMessageContainer";
import LabeledCheckbox from "../shared-components/LabeledCheckbox";
import Divider from "../shared-components/LineDivider";
import { Modal } from "../shared-components/Modal";
import { LabeledValueName, PostRatingBehavior } from "../utils/types";
import { contentRatingsAndImagesUrlFromPost } from "./helpers";
import SystemMediaPostHeader from "./SystemMediaPostHeader";
import { behaviorMap, useFamaFlags } from "../api/FamaFlags";

export type SystemEditMediaModalActions = "cancel" | "save";

type SystemModalState = {
  isOpen: boolean;
  post?: ItemWithMetaData;
  behaviors: string[];
  item?: {
    postContent: any;
    ratings: Rating[];
    postImageUrls: MediaExternalInternalURLs[];
  };
  actionHandler?: (
    action: SystemEditMediaModalActions,
    ratings: PostRatingBehavior[]
  ) => void;
};

const useSystemEditMediaModal = (
  editActionHandler: (
    action: SystemEditMediaModalActions,
    post: PostRatingBehavior[]
  ) => void
) => {
  const [state, setState] = useState<SystemModalState>({
    isOpen: false,
    behaviors: [],
  });
  const [error, setError] = useState<string>("");
  const [ratingSelections, setRatingSelections] = useState<
    PostRatingBehavior[]
  >([]);

  function cancel() {
    hide();
  }

  function show(
    post: ItemWithMetaData,
    behaviors: string[],
    actionHandler?: (
      action: SystemEditMediaModalActions,
      ratings: PostRatingBehavior[]
    ) => void
  ) {
    const postContent = contentRatingsAndImagesUrlFromPost(post);
    setState({
      isOpen: true,
      behaviors: behaviors,
      post,
      item: { ...postContent },
      actionHandler,
    });
  }
  function hide() {
    setState({ ...state, isOpen: false });
  }

  function onCheckboxChange(ratings: PostRatingBehavior[]) {
    setRatingSelections(ratings);
  }

  function onSave() {
    setError("");
    if (ratingSelections.length === 0) {
      setError("Please select at least one rating");
      return;
    }

    editActionHandler("save", ratingSelections);
    hide();
  }

  const modal = (
    <Modal
      open={state.isOpen}
      onClose={cancel}
      title={""}
      width={"w-full max-w-lg"}
    >
      <div>
        <SystemMediaPostHeader
          postUrl={state.item?.postContent.url}
          profileUrl={state.item?.postContent.profile_url}
          datePosted={state.item?.postContent.created_at}
          isReadOnly={true}
          showLink={false}
        />
      </div>
      <div className="flex flex-col gap-4">
        <div>
          {state.item?.postImageUrls && (
            <PostImage media={state.item?.postImageUrls} />
          )}
        </div>
        <div className="flex-1">
          {state.item?.postContent.text && (
            <div className="font-normal text-base leading-6 text-gray-600">
              {state.item?.postContent.text}
            </div>
          )}
          <div className="flex mt-2 flex-wrap gap-3">
            <BehaviorCheckboxGroup
              behaviors={state.behaviors}
              initialSelection={state.item?.ratings ?? []}
              onChange={onCheckboxChange}
            />
          </div>
        </div>
      </div>
      {error && <ErrorMessageContainer message={error} />}
      <Divider addMargin={false} />
      <div className="flex gap-4 mt-4">
        <Button
          text={"Cancel"}
          colorType={ButtonColorTypes.cancel}
          onClick={cancel}
          className="flex-1"
        />
        <Button
          text="Save"
          colorType={ButtonColorTypes.primary}
          onClick={onSave}
          className="flex-1"
        />
      </div>
    </Modal>
  );

  return { show, hide, modal };
};

export function BehaviorCheckboxGroup({
  initialSelection,
  behaviors,
  onChange,
}: {
  initialSelection: Rating[];
  behaviors: string[];
  onChange: (postRatingBehaviors: PostRatingBehavior[]) => void;
}) {
  const { behaviorMappingActions } = useFamaFlags();
  // eslint-disable-next-line
  const [postRatings, setPostRatings] = useState<PostRatingBehavior[]>(
    initialSelection.map((selection) => ({
      name: selection.name,
      score_type: selection.score_type as "behavior",
    }))
  );
  const [flagList, setFlagList] = useState<LabeledValueName[]>([]);

  useEffect(() => {
    const behaviorsOptions: string[] = [];
    behaviors.forEach((behavior) => {
      if (!behaviorsOptions.includes(behavior)) {
        behaviorsOptions.push(behavior);
      }
    });
    const newBehaviorOptions = behaviorsOptions.map((value) => {
      return {
        name: value,
        label: value,
        checked: !!initialSelection.find(
          (sel) => sel.name.toLocaleLowerCase() === value.toLocaleLowerCase()
        ),
      };
    });
    setFlagList(newBehaviorOptions);
  }, [behaviors, initialSelection, setFlagList]);

  function handleOnChange(event: React.ChangeEvent<HTMLInputElement>) {
    const name = event.target.name;
    const checked = event.target.checked;

    const checkedList = flagList.map((item) => {
      if (item.name === name) {
        return { ...item, checked: checked };
      }
      return item;
    });

    setFlagList(checkedList);

    let ratingBehaviorList: PostRatingBehavior[] = checkedList
      .filter((flag) => flag.checked)
      .map((flag) => ({
        name: flag.name,
        score_type: "behavior",
      }));

    setPostRatings(ratingBehaviorList);
    onChange(ratingBehaviorList);
  }

  return (
    <>
      {flagList.map((item) => {
        return (
          <div
            key={item.name}
            className="capitalize text-base leading-6 font-normal my-2"
          >
            <LabeledCheckbox
              name={item.name}
              label={behaviorMap(item.name, behaviorMappingActions, "key")}
              checked={item.checked}
              onChange={handleOnChange}
            />
          </div>
        );
      })}
    </>
  );
}

export default useSystemEditMediaModal;
